import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { Fragment, ReactNode, createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { TooltipV2 } from '../tooltip/TooltipV2';

const styles = {
    textColor: {
        withError: 'text-red-800 dark:text-red-400 placeholder-red-800 dark:placeholder-red-400 ',
        withoutError: 'text-gray-500',
    },
    borderStyle: {
        border: 'border rounded-md dark:bg-white/5',
        underline: 'border-0 border-b rounded-none dark:bg-transparent',
    },
    focusedStyle: {
        border: {
            withError: 'focus-within:outline-none focus-within:ring-0 border-red-600 dark:border-red-400',
            withoutError:
                'focus-within:outline-none focus-within:ring-1 focus-within:ring-indigo-600 focus-within:border-indigo-600 focus-within:dark:border-indigo-400',
        },
        underline: {
            withError:
                'focus-within:outline-none focus-within:ring-0 focus-within:border-b-1 focus-within:border-red-600 focus-within:dark:border-red-400',
            withoutError:
                'focus-within:outline-none focus-within:ring-0 focus-within:border-b-1 focus-within:border-indigo-600 focus-within:dark:border-indigo-400',
        },
    },
};

export const InputV2Context = createContext({ disabled: false });

export type InputV2Props = {
    title?: string;
    description?: string;
    placeholder?: string;
    trailingText?: string;
    disabledHint?: string;
    hint?: string;
    shortcutKey?: string;
    requiresCmdKey?: boolean;
    isError?: boolean;
    errorMessage?: string;
    borderStyle?: 'border' | 'underline';
    textAlign?: 'left' | 'right' | 'center';
    type: 'text' | 'date';
    value?: string;
    options?: string[];
    isOptional?: boolean;
    leftIcon?: ReactNode;
    children?: ReactNode;
    characterLimit?: number;
    showCharacterLimitHint?: boolean;
    inputRef?: React.RefObject<HTMLInputElement>;
    onChange?: (value: string) => void;
    validator?: (value: string) => boolean;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'value' | 'onChange'>;

function InputV2({
    title,
    description,
    placeholder,
    trailingText,
    disabledHint,
    hint,
    shortcutKey,
    requiresCmdKey,
    isError,
    errorMessage,
    borderStyle = 'border',
    textAlign = 'left',
    type,
    value,
    options,
    isOptional,
    leftIcon,
    inputRef,
    characterLimit,
    showCharacterLimitHint = characterLimit !== undefined,
    onChange,
    validator,
    children,
    ...props
}: InputV2Props): JSX.Element {
    inputRef = useRef<HTMLInputElement | null>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const [showDropdown, setShowDropdown] = useState(false);

    const detectHotKey = useCallback(
        (e: KeyboardEvent) => {
            const keyLookups: { [key: string]: string } = {
                BracketLeft: 'key[',
                BracketRight: 'key]',
            };

            const _eventKey = keyLookups[e.code] || e.code.toLowerCase();
            if (
                _eventKey === `key${shortcutKey.toLowerCase()}` &&
                !['[object HTMLTextAreaElement]', '[object HTMLInputElement]'].includes(e.target.toString())
            ) {
                if (requiresCmdKey) {
                    if (e.metaKey) {
                        e.preventDefault();
                        inputRef.current.focus();
                    }
                } else {
                    inputRef.current.focus();
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [shortcutKey]
    );

    function detectArrowDown(e: KeyboardEvent): void {
        if (e.key === 'ArrowDown' && !showDropdown) {
            buttonRef.current.click();
        }
    }

    function handleChange(value?: string): void {
        if (!onChange || value === undefined) {
            return;
        }

        const val = characterLimit !== undefined ? value.substring(0, characterLimit) : value;

        if (!validator) {
            onChange(val);
        } else {
            if (validator(val)) {
                onChange(val);
            }
        }
    }

    useEffect(() => {
        if (shortcutKey) {
            document.addEventListener('keydown', detectHotKey);
            return (): void => document.removeEventListener('keydown', detectHotKey);
        } else if (options) {
            document.addEventListener('keydown', detectArrowDown);
            return (): void => document.removeEventListener('keydown', detectArrowDown);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect((): void => {
        if (props.autoFocus && inputRef.current) {
            inputRef.current.focus();
        }
    }, [props.autoFocus]);

    return (
        <div className="grid gap-y-1">
            {(title || isOptional) && (
                <div className="flex justify-between text-sm">
                    {title && <label className="block text-gray-700 dark:text-gray-200 font-medium">{title}</label>}
                    {isOptional && <span className="text-gray-500">Optional</span>}
                </div>
            )}

            {description && <span className="text-sm font-normal text-gray-600 dark:text-gray-400">{description}</span>}

            <TooltipV2 message={disabledHint} position="right" disabled={!props.disabled || !disabledHint}>
                <div
                    className={`${props.disabled ? (value === undefined ? 'opacity-50' : 'bg-gray-50 dark:bg-white/[0.11]') : ''} ${
                        styles.borderStyle[borderStyle]
                    } ${
                        styles.focusedStyle[borderStyle][isError || errorMessage ? 'withError' : 'withoutError']
                    } flex items-center py-1.5 ${options ? 'pl-3 pr-2' : 'px-3'} gap-x-2 border-gray-200 dark:border-white/[0.07] relative`}
                >
                    {children && <div className="pr-12">{children}</div>}
                    {leftIcon && (
                        <div
                            className={`pointer-events-none absolute w-4 h-4 flex items-center shrink-0 ${
                                isError || errorMessage ? styles.textColor.withError : styles.textColor.withoutError
                            }`}
                        >
                            {leftIcon}
                        </div>
                    )}
                    <input
                        {...props}
                        data-date-picker
                        type={type}
                        ref={inputRef}
                        value={value}
                        className={`${leftIcon ? 'pl-6' : ''} border-0 p-0 focus:ring-0 w-full text-sm ${
                            props.disabled ? 'text-gray-500 dark:text-gray-400' : 'text-gray-800 dark:text-gray-200'
                        } bg-transparent ${
                            isError || errorMessage ? styles.textColor.withError : styles.textColor.withoutError
                        } font-normal focus:outline-0`}
                        placeholder={placeholder}
                        onChange={(e): void => handleChange(e.target.value)}
                        style={{ textAlign }}
                    />
                    {options?.length > 0 && (
                        <Menu>
                            <Menu.Button
                                ref={buttonRef}
                                onClick={(): void => setShowDropdown(!showDropdown)}
                                className="ml-auto flex grow items-center focus:outline-none"
                            >
                                <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                            </Menu.Button>
                            <Transition
                                as={Fragment}
                                enter="transition ease-out duration-100"
                                enterFrom="transform opacity-0 scale-95"
                                enterTo="transform opacity-100 scale-100"
                                leave="transition ease-in duration-75"
                                leaveFrom="transform opacity-100 scale-100"
                                leaveTo="transform opacity-0 scale-95"
                            >
                                <Menu.Items className="grid w-full absolute top-[38px] right-0 max-h-72 overflow-y-auto overflow-x-hidden rounded-md border bg-white border-gray-200 dark:bg-white/5 dark:border-white/[0.07] py-1 focus:outline-none">
                                    {options.map((option, idx) => (
                                        <Menu.Item key={idx}>
                                            {({ active }): JSX.Element => (
                                                <button
                                                    type="button"
                                                    className={`w-full text-left text-sm py-2 pl-3 pr-4 cursor-pointer ${
                                                        active
                                                            ? 'bg-indigo-600 dark:bg-indigo-400 text-white dark:text-gray-50'
                                                            : 'text-gray-700 dark:text-gray-200'
                                                    }`}
                                                    onMouseDown={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
                                                        e.preventDefault();
                                                    }}
                                                    onClick={(): void => {
                                                        setShowDropdown(false);
                                                        handleChange(option);
                                                        setTimeout(function () {
                                                            inputRef.current?.focus();
                                                            inputRef.current.selectionStart = inputRef.current.selectionEnd = option.length;
                                                        }, 15);
                                                    }}
                                                >
                                                    {option}
                                                </button>
                                            )}
                                        </Menu.Item>
                                    ))}
                                </Menu.Items>
                            </Transition>
                        </Menu>
                    )}
                    {!options && trailingText && <span className="text-gray-400 text-sm font-normal">{trailingText}</span>}
                    {!options && shortcutKey && (
                        <kbd className="font-medium py-0.5 px-2 -mr-1 -my-1 rounded-[5px] font-sans text-gray-400 border border-gray-200 dark:border-white/[0.07] text-sm flex gap-x-[1px]">
                            <div>{requiresCmdKey && '⌘'}</div>
                            <div>{shortcutKey.toUpperCase()}</div>
                        </kbd>
                    )}
                </div>
            </TooltipV2>

            {/* HINT */}
            {hint && !errorMessage && <p className="text-sm text-gray-500">{hint}</p>}

            {/* ERROR MESSAGE */}
            {errorMessage && <p className={`text-sm font-normal ${styles.textColor.withError}`}>{errorMessage}</p>}

            {characterLimit !== undefined && !errorMessage && showCharacterLimitHint && (
                <p className="text-sm text-gray-400 ml-auto">
                    {value?.length || 0} / {characterLimit}
                </p>
            )}
        </div>
    );
}

type DropdownProps = {
    options: { label: string; value: string }[];
    value?: string;
    name: string;
} & React.SelectHTMLAttributes<HTMLSelectElement>;

function Dropdown({ options, name, value, ...props }: DropdownProps): JSX.Element {
    const { disabled } = useContext(InputV2Context);

    return (
        <div className={`absolute inset-y-0 left-0 flex items-center ${disabled ? ' cursor-default' : 'cursor-pointer'}`}>
            <select
                disabled={disabled}
                id={name}
                name={name}
                value={value}
                autoComplete={name}
                className="h-full rounded-md border-transparent bg-transparent py-0 pl-2 pr-8 text-gray-500 focus:border-transparent focus:ring-transparent text-sm"
                {...props}
            >
                {options.map(({ value, label }) => {
                    return (
                        <option key={value} value={value}>
                            {label}
                        </option>
                    );
                })}
            </select>
        </div>
    );
}

InputV2.Dropdown = Dropdown;

export default InputV2;
